import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart3, Download, FileText, FileSpreadsheet, Presentation, Lightbulb, Play, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FreeResourcesPage = () => {
  const { toast } = useToast();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API}/resources/download/${fileId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: 'Download Started',
        description: `Downloading ${filename}`
      });
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Failed to download file.',
        variant: 'destructive'
      });
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === '.pdf') return FileText;
    if (fileType === '.xls' || fileType === '.xlsx') return FileSpreadsheet;
    if (fileType === '.pptx' || fileType === '.ppt') return Presentation;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Static presentation ideas
  const presentationIdeas = [
    {
      id: 1,
      title: 'Board Deck Template',
      description: 'Professional 14-slide quarterly board presentation with KPIs, financials, and department updates.',
      type: 'Interactive',
      link: '/presentations'
    },
    {
      id: 2,
      title: 'Investor Pitch Deck Structure',
      description: 'Proven 12-slide structure that top VCs expect: Problem, Solution, Market, Traction, Team, Ask.',
      type: 'Guide',
      link: '/articles/investor-ready-financial-model'
    },
    {
      id: 3,
      title: 'Monthly Financial Review',
      description: 'Template for presenting monthly financials to leadership: actuals vs budget, variance analysis, forecasts.',
      type: 'Template',
      link: '/presentations'
    },
    {
      id: 4,
      title: 'QBR (Quarterly Business Review)',
      description: 'Comprehensive quarterly review template covering all business units, metrics, and strategic initiatives.',
      type: 'Template',
      link: '/presentations'
    },
    {
      id: 5,
      title: 'Fundraising Data Room',
      description: 'Checklist and structure for organizing your data room during due diligence.',
      type: 'Checklist',
      link: '/articles/financial-due-diligence-prep'
    },
    {
      id: 6,
      title: 'Annual Planning Presentation',
      description: 'Framework for presenting annual strategic plans, budgets, and OKRs to the board.',
      type: 'Framework',
      link: '/presentations'
    },
  ];

  // Static downloadable templates
  const staticDownloads = [
    {
      id: 'static-1',
      name: 'Financial Model Template',
      description: 'Complete 3-statement model with revenue drivers, P&L, balance sheet, and cash flow.',
      type: 'Excel',
      icon: FileSpreadsheet,
      size: '2.4 MB'
    },
    {
      id: 'static-2',
      name: 'SaaS Metrics Calculator',
      description: 'Calculate LTV, CAC, Magic Number, NRR, and other key SaaS metrics automatically.',
      type: 'Excel',
      icon: FileSpreadsheet,
      size: '1.8 MB'
    },
    {
      id: 'static-3',
      name: 'Startup KPI Dashboard',
      description: 'Pre-built dashboard template for tracking essential startup metrics.',
      type: 'Excel',
      icon: FileSpreadsheet,
      size: '1.2 MB'
    },
    {
      id: 'static-4',
      name: 'Cash Flow Forecast Template',
      description: '13-week rolling cash flow forecast with scenario planning capabilities.',
      type: 'Excel',
      icon: FileSpreadsheet,
      size: '890 KB'
    },
    {
      id: 'static-5',
      name: 'Board Reporting Package',
      description: 'Monthly board report template with executive summary, financials, and KPIs.',
      type: 'PDF',
      icon: FileText,
      size: '1.5 MB'
    },
    {
      id: 'static-6',
      name: 'Due Diligence Checklist',
      description: 'Complete checklist for preparing financials for investor due diligence.',
      type: 'PDF',
      icon: FileText,
      size: '420 KB'
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4 sm:py-6 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
            <div className="relative">
              <TrendingUp className="h-5 w-5 sm:h-7 sm:w-7 text-[#C9A64A]" />
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-lg sm:text-xl font-bold text-[#1A1A1C] tracking-tight">
              Virtual<span className="text-[#C9A64A]">CFO</span>
            </span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="gap-2 text-sm">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 bg-gradient-to-b from-[#0F1A2A] to-[#1A1A1C] text-white">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 px-4 sm:px-6 py-2 sm:py-3 bg-[#C9A64A]/20 rounded-full border border-[#C9A64A]/30">
            <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-[#C9A64A]" />
            <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">Free Resources</span>
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6">Free Financial Resources</h1>
          <p className="text-base sm:text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-3xl mx-auto px-4">
            Download our collection of financial models, templates, and presentation ideas. Built by CFOs, designed for founders.
          </p>
        </div>
      </section>

      {/* Presentation Ideas Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-[#F8F8F8] to-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="flex items-center gap-3 mb-3">
              <Lightbulb className="h-6 w-6 sm:h-8 sm:w-8 text-[#C9A64A]" />
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1C]">Presentation Ideas</h2>
            </div>
            <p className="text-sm sm:text-base text-[#5A6270]">
              Templates and frameworks for investor pitches, board meetings, and financial reviews.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {presentationIdeas.map((idea) => (
              <Link key={idea.id} to={idea.link}>
                <div className="h-full bg-white p-5 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-200 hover:shadow-lg hover:border-[#C9A64A] transition-all duration-300 group cursor-pointer">
                  <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#C9A64A] to-[#D4B76A] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Presentation className="h-5 w-5 sm:h-6 sm:w-6 text-[#0F1A2A]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-[#C9A64A] uppercase tracking-wider">{idea.type}</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-[#1A1A1C] group-hover:text-[#C9A64A] transition-colors">
                        {idea.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-sm text-[#5A6270] mb-4">{idea.description}</p>
                  <div className="flex items-center text-[#C9A64A] font-semibold text-sm group-hover:gap-2 transition-all">
                    <span>View Resource</span>
                    <ExternalLink className="h-4 w-4 ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Link to full presentations page */}
          <div className="mt-8 text-center">
            <Link to="/presentations">
              <Button className="bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 font-semibold">
                <Play className="mr-2 h-4 w-4" />
                View Interactive Board Deck
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Downloads Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="flex items-center gap-3 mb-3">
              <Download className="h-6 w-6 sm:h-8 sm:w-8 text-[#C9A64A]" />
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1C]">Downloads</h2>
            </div>
            <p className="text-sm sm:text-base text-[#5A6270]">
              Excel templates, PDF guides, and financial models ready to use.
            </p>
          </div>

          {/* Static Downloads */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {staticDownloads.map((file) => {
              const Icon = file.icon;
              return (
                <div
                  key={file.id}
                  className="bg-white p-5 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-200 hover:shadow-lg hover:border-[#C9A64A] transition-all duration-300 group"
                >
                  <div className="flex items-start gap-3 sm:gap-4 mb-3">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#C9A64A]/10 flex items-center justify-center group-hover:bg-[#C9A64A]/20 transition-colors">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-[#C9A64A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-[#5A6270] uppercase">{file.type}</span>
                        <span className="text-xs text-[#5A6270]">•</span>
                        <span className="text-xs text-[#5A6270]">{file.size}</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-[#1A1A1C] truncate">
                        {file.name}
                      </h3>
                    </div>
                  </div>
                  <p className="text-sm text-[#5A6270] mb-4">{file.description}</p>
                  <Button
                    className="w-full bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 font-semibold text-sm"
                    size="sm"
                    onClick={() => toast({ title: 'Coming Soon', description: 'This template will be available for download shortly.' })}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Dynamic Resources from Backend */}
          {loading ? (
            <div className="text-center py-8 sm:py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-[#C9A64A] border-t-transparent"></div>
              <p className="text-[#5A6270] mt-4 text-sm sm:text-base">Loading additional resources...</p>
            </div>
          ) : resources.length > 0 && (
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-[#1A1A1C] mb-4 sm:mb-6">Additional Uploads</h3>
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                {resources.map((resource) => {
                  const Icon = getFileIcon(resource.file_type);
                  return (
                    <div
                      key={resource.id}
                      className="bg-white p-5 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-200 hover:shadow-lg hover:border-[#C9A64A] transition-all duration-300 group"
                    >
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#C9A64A]/10 flex items-center justify-center group-hover:bg-[#C9A64A]/20 transition-colors">
                          <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-[#C9A64A]" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-bold text-[#1A1A1C] mb-1 truncate">
                            {resource.original_filename}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-[#5A6270] mb-3 sm:mb-4">
                            <span>{formatFileSize(resource.file_size)}</span>
                            <span className="hidden sm:inline">•</span>
                            <span>{formatDate(resource.upload_date)}</span>
                            <span className="hidden sm:inline">•</span>
                            <span>{resource.download_count} downloads</span>
                          </div>
                          
                          <Button
                            onClick={() => handleDownload(resource.id, resource.original_filename)}
                            className="bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 font-semibold text-sm"
                            size="sm"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-r from-[#0F1A2A] to-[#1A1A1C] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
            Need Custom Financial Models?
          </h2>
          <p className="text-base sm:text-xl text-gray-300 font-light mb-6 sm:mb-10 leading-relaxed px-4">
            These templates are a starting point. Our Virtual CFO team can build custom models tailored to your specific business needs.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 font-bold px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg">
              Talk to a Virtual CFO
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
